import { useState } from "react";
import { Megaphone, Pencil, Plus, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { Loader, EmptyState } from "../common/States";
import { ANN_CATEGORY, ANN_AUDIENCE, ANN_STATE } from "../../lib/commsLabels";
import { fmtDate } from "../../lib/labels";

const local = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");

const Form = ({ item, categories, audiences, allowEmail, onSave, onClose }) => {
  const [f, setF] = useState({ title: item?.title || "", body: item?.body || "", category: item?.category || categories[0],
    audience: item?.audience || audiences[0], starts_at: local(item?.starts_at), ends_at: local(item?.ends_at), status: item?.status || "draft", send_email: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = () => onSave({ ...f, starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : null, ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : null });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="sm:max-w-xl" data-testid="announcement-dialog">
        <DialogHeader><DialogTitle>{item ? "Modifier l'annonce" : "Nouvelle annonce"}</DialogTitle></DialogHeader>
        <Input placeholder="Titre" value={f.title} onChange={set("title")} data-testid="ann-title-input" />
        <Textarea rows={5} placeholder="Message" value={f.body} onChange={set("body")} data-testid="ann-body-input" />
        <div className="grid gap-2 sm:grid-cols-2">
          <select className="field-select" value={f.category} onChange={set("category")} data-testid="ann-category-select">{categories.map((c) => <option key={c} value={c}>{ANN_CATEGORY[c]}</option>)}</select>
          <select className="field-select" value={f.audience} onChange={set("audience")} data-testid="ann-audience-select">{audiences.map((c) => <option key={c} value={c}>{ANN_AUDIENCE[c]}</option>)}</select>
          <label className="text-xs text-slate-500">Début de publication<Input type="datetime-local" value={f.starts_at} onChange={set("starts_at")} data-testid="ann-start-input" /></label>
          <label className="text-xs text-slate-500">Fin (facultatif)<Input type="datetime-local" value={f.ends_at} onChange={set("ends_at")} data-testid="ann-end-input" /></label>
          <select className="field-select" value={f.status} onChange={set("status")} data-testid="ann-status-select"><option value="draft">Brouillon</option><option value="published">Publiée</option><option value="archived">Archivée</option></select>
        </div>
        {allowEmail && !item && <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.send_email} onCheckedChange={(v) => setF({ ...f, send_email: !!v })} data-testid="ann-email-checkbox" />Envoyer aussi par email aux entreprises (communication de service)</label>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button><Button onClick={save} data-testid="ann-save-button">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AnnouncementsManager({ base, categories, audiences, allowEmail = false, canManage = true }) {
  const { data, loading, reload } = useApi(base);
  const [editing, setEditing] = useState(null);
  const save = async (body) => {
    try {
      if (editing?.id) await api.put(`${base}/${editing.id}`, body); else await api.post(base, body);
      toast.success("Annonce enregistrée"); setEditing(null); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (a) => { try { await api.delete(`${base}/${a.id}`); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  if (loading) return <Loader />;
  return (
    <div className="space-y-3" data-testid="announcements-manager">
      {canManage && <Button onClick={() => setEditing({})} data-testid="ann-new-button"><Plus className="mr-1.5 h-4 w-4" />Nouvelle annonce</Button>}
      {!data.length && <EmptyState icon={Megaphone} title="Aucune annonce" />}
      {data.map((a) => (
        <div key={a.id} className="card-surface flex flex-col gap-2 p-4 sm:flex-row sm:items-start" data-testid={`ann-${a.id}`}>
          <div className="flex-1">
            <p className="font-medium">{a.title} <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs" data-testid={`ann-state-${a.id}`}>{ANN_STATE[a.state]}</span></p>
            <p className="mt-1 line-clamp-2 text-sm text-slate-600">{a.body}</p>
            <p className="mt-1 text-xs text-slate-400">{ANN_CATEGORY[a.category]} · {ANN_AUDIENCE[a.audience]} · du {fmtDate(a.starts_at)}{a.ends_at ? ` au ${fmtDate(a.ends_at)}` : " (sans fin)"}
              {" · "}<Eye className="inline h-3 w-3" /> {a.views} vue(s){a.email_stats ? ` · emails acceptés : ${a.email_stats.accepted}, refusés : ${a.email_stats.failed}` : ""}</p>
          </div>
          {canManage && <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => setEditing(a)} data-testid={`ann-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(a)} data-testid={`ann-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>}
        </div>
      ))}
      {editing && <Form item={editing.id ? editing : null} categories={categories} audiences={audiences} allowEmail={allowEmail} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}
